import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard, { ProductCardSkeleton } from "@/components/storefront/ProductCard";
import CinematicHero from "@/components/storefront/CinematicHero";
import { SectionHeader, ErrorNotice } from "@/components/storefront/common";
import Community from "@/components/storefront/Community";
import { directionsUrl } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import {
  ShieldCheck, Lock, Truck, BadgeCheck, Headphones, Store, ArrowRight, MapPin, Clock, Phone, Wrench, Navigation, Mail,
} from "lucide-react";

const ICONS = { "shield-check": ShieldCheck, lock: Lock, truck: Truck, "badge-check": BadgeCheck, headphones: Headphones, store: Store };

function Row({ title, subtitle, to, products, loading }) {
  if (!loading && (!products || products.length === 0)) return null;
  return (
    <section className="container-x py-8 sm:py-10">
      <SectionHeader title={title} subtitle={subtitle} to={to} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)
          : products.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

// Banners and hero config are kept for the lifetime of this page load (module
// scope, no storage) so returning to the homepage paints instantly instead of
// flashing placeholders; both are still refreshed in the background on every mount.
let bannerCache = null;
let heroCache = null;

export default function Home() {
  const { settings, settingsLoading, settingsError, business } = useStore();
  const [data, setData] = useState({ cats: [], brands: [], trending: [], deals: [], newArrival: [], bestSeller: [] });
  const [promoBanners, setPromoBanners] = useState(bannerCache || []);
  const [hero, setHero] = useState(heroCache);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = () => { setError(null); setReloadKey((k) => k + 1); };

  // Promotional banners (the "Featured offers" band below the hero). Kept in
  // module scope so returning to the homepage paints instantly; refreshed here.
  useEffect(() => {
    let active = true;
    api.get("/banners").then(({ data }) => {
      if (!active) return;
      bannerCache = data.banners || [];
      setPromoBanners(bannerCache);
    })
      .catch((err) => { if (active) setError(apiError(err)); });
    return () => { active = false; };
  }, [reloadKey]);

  // Active hero image config. Fetched independently of the banners; on failure the
  // last good value (or the bundled default) is kept so the hero never blanks.
  useEffect(() => {
    let active = true;
    api.get("/homepage/hero").then(({ data }) => {
      if (!active) return;
      heroCache = data;
      setHero(data);
    }).catch(() => { /* keep last good hero image */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.title = business?.name ? `${business.name} — Genuine Accessories & Gadgets` : "Genuine Accessories & Gadgets";
    setLoading(true);
    // `/homepage` used to be fetched here purely to duplicate the banner list
    // (without its active/date filtering) for the static hero fallback — the one
    // date-aware `/banners` list above now serves both, and the sections it
    // returns were never rendered on the storefront.
    Promise.all([
      api.get("/categories"),
      api.get("/brands"),
      api.get("/products?trending=true&limit=8"),
      api.get("/products?sort=discount&limit=8"),
      api.get("/products?newArrival=true&limit=8"),
      api.get("/products?bestSeller=true&limit=8"),
    ]).then(([c, b, t, d, n, bs]) => {
      setData({
        cats: c.data.categories, brands: b.data.brands, trending: t.data.products,
        deals: d.data.products, newArrival: n.data.products, bestSeller: bs.data.products,
      });
    }).catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  }, [business, reloadKey]);

  // Promo-placement banner (the "Featured offers" band below the trust bar).
  const promo = promoBanners.find((x) => x.placement === "promo");
  const trust = settings?.trust || [];

  return (
    <div>
      <CinematicHero bgImageSrc={hero?.image} />

      {error && (
        <section className="container-x pt-4">
          <ErrorNotice title="Some store content could not be loaded" text={error} onRetry={retry} />
        </section>
      )}

      {/* Trust bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="container-x grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 py-5">
          {trust.map((t, i) => {
            const Icon = ICONS[t.icon] || ShieldCheck;
            return (
              <div key={i} className="flex items-center gap-2.5">
                <Icon className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{t.title}</p>
                  <p className="text-[11px] text-slate-500 truncate">{t.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Categories */}
      <section className="container-x py-8 sm:py-10">
        <SectionHeader title="Shop by Category" subtitle="Find exactly what you need" to="/categories" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {(loading ? Array.from({ length: 6 }) : data.cats).map((c, i) => (
            c ? (
              <Link key={c.id} to={`/category/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="group rounded-xl border border-slate-200 overflow-hidden bg-white hover:shadow-lg transition">
                <div className="aspect-square bg-slate-50 overflow-hidden">
                  <img src={resolveMediaUrl(c.image)} alt={c.name} loading="lazy" onError={handleMediaError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-2.5 text-center text-sm font-semibold text-slate-800">{c.name}</div>
              </Link>
            ) : <div key={i} className="rounded-xl border border-slate-200 aspect-[3/4] bg-slate-100 animate-pulse" />
          ))}
        </div>
      </section>

      {/* Brands */}
      {data.brands.length > 0 && (
        <section className="container-x py-6">
          <SectionHeader title="Shop by Brand" subtitle="Trusted brands you love" to="/brands" />
          <div className="flex flex-wrap gap-3">
            {data.brands.map((b) => (
              <Link key={b.id} to={`/brand/${b.slug}`} data-testid={`home-brand-${b.slug}`}
                className="px-5 py-3 rounded-xl border border-slate-200 bg-white hover:border-amber-400 hover:shadow-sm font-semibold text-slate-800 transition">
                {b.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <Row title="Trending Now" subtitle="What everyone's buying" to="/shop" products={data.trending} loading={loading} />
      <Row title="Best Deals" subtitle="Biggest savings right now" to="/shop?sort=discount" products={data.deals} loading={loading} />

      {/* Promo banner */}
      {promo && (
        <section className="container-x py-8">
          <Link to={promo.ctaLink || "/shop"} className="block relative rounded-2xl overflow-hidden bg-slate-900 group" data-testid="promo-banner">
            <img src={resolveMediaUrl(promo.image)} alt={promo.title} onError={handleMediaError} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition duration-700" />
            <div className="relative p-8 sm:p-14 max-w-lg">
              <h3 className="text-2xl sm:text-4xl font-bold text-white font-display">{promo.title}</h3>
              <p className="text-slate-200 mt-2">{promo.subtitle}</p>
              <span className="inline-flex items-center gap-2 mt-5 bg-amber-500 text-slate-900 font-semibold px-5 py-2.5 rounded-full">{promo.ctaText || "Shop"} <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
        </section>
      )}

      <Row title="New Arrivals" subtitle="Fresh in store" to="/shop?newArrival=true" products={data.newArrival} loading={loading} />
      <Row title="Popular Products" subtitle="Customer favourites" to="/shop?bestSeller=true" products={data.bestSeller} loading={loading} />

      {/* Store section */}
      <section className="container-x py-10">
        <div className="grid lg:grid-cols-2 gap-6 items-center bg-slate-50 rounded-2xl overflow-hidden border border-slate-200">
          <div className="p-8 sm:p-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">Visit Us</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-display mt-2">Come by our store</h2>
            <p className="text-slate-600 mt-2">See and try products in person before you buy.</p>
            <div className="mt-6 space-y-3 text-sm text-slate-700">
              {settingsLoading ? (
                <>
                  <div className="h-4 w-64 rounded bg-slate-200 animate-pulse" />
                  <div className="h-4 w-44 rounded bg-slate-200 animate-pulse" />
                  <div className="h-4 w-36 rounded bg-slate-200 animate-pulse" />
                </>
              ) : settingsError ? (
                <p className="text-slate-500">Store details are temporarily unavailable.</p>
              ) : (
                <>
                  <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-amber-500" /> {[business?.address, business?.city].filter(Boolean).join(", ") || "Not available"}</div>
                  <div className="flex items-center gap-3"><Clock className="h-5 w-5 text-amber-500" /> {business?.storeHours || "Not available"}</div>
                  <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-amber-500" /> {business?.phone || "Not available"}</div>
                </>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={directionsUrl(business)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg" data-testid="home-directions"><Navigation className="h-4 w-4" /> Get Directions</a>
              {business?.phone && <a href={`tel:${business.phone}`} className="inline-flex items-center gap-2 border border-slate-300 text-sm font-semibold px-4 py-2.5 rounded-lg"><Phone className="h-4 w-4" /> Call Store</a>}
              <Link to="/store" className="inline-flex items-center gap-2 border border-slate-300 text-sm font-semibold px-4 py-2.5 rounded-lg">Store details <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
          <div className="h-64 lg:h-full min-h-[280px]">
            <img src={resolveMediaUrl(business?.storePhoto || "https://images.pexels.com/photos/30666637/pexels-photo-30666637.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")} alt="Shree Shyam Mobile store" onError={handleMediaError} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Services teaser */}
      <section className="container-x py-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 flex flex-col sm:flex-row items-center gap-6">
          <span className="h-14 w-14 rounded-2xl bg-slate-900 grid place-items-center shrink-0"><Wrench className="h-7 w-7 text-amber-400" /></span>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-display">In-Store Services</h2>
            <p className="text-slate-500 mt-1 text-sm">Phone glass replacement, accessory fitting and more — available at our Bilaspur store.</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-5 py-3 rounded-lg transition shrink-0" data-testid="home-services-cta">View Services <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Community */}
      <Community />

      {/* Final CTA */}
      <section className="container-x pb-12">
        <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-display">Ready to upgrade your gear?</h2>
          <p className="text-slate-300 mt-2">Explore our full range of genuine accessories.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-7 py-3.5 rounded-full transition" data-testid="final-cta">
            Explore Shop <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
